import { UseQueryResult, keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useNavigation, useSearchParams } from 'react-router';

import { isPendingAccountSwitch } from '~/routing/isPendingAccountSwitch';
import { getSessionPlQueryOptions } from '~/routing/sessionPlQueryOptions';
import { SessionPlQueryReturnData, handleGetClientSessionInfo } from '~/services/ClientServiceTS';
import { getAccountDetailsFromURL } from '~/utils/utils';

export { getSessionPlQueryKey } from '~/routing/sessionPlQueryOptions';

const useSessionPlQuery = <TData = SessionPlQueryReturnData>(
  select?: (data: SessionPlQueryReturnData) => TData,
  options?: { enabled?: boolean },
) => {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { accountId, accountType } =
    getAccountDetailsFromURL(searchParams.get('referer') || location.pathname) || {};
  const hasAccountInPath = !!getAccountDetailsFromURL(location.pathname)?.accountId;

  // Admin / SessionTimer stay mounted above the outlet during account switch.
  // Pause observers so they do not refetch the previous account's session.pl
  // while middleware resolves the destination session.
  const switchingAccount = isPendingAccountSwitch({
    navigationState: navigation.state,
    currentAccountId: accountId,
    currentAccountType: accountType,
    nextPathname: navigation.location?.pathname,
  });

  const sessionQueryOptions = getSessionPlQueryOptions({ accountId, accountType });

  return useQuery({
    ...sessionQueryOptions,
    queryFn: async (ctx) => {
      const data = await sessionQueryOptions.queryFn!(ctx);
      if (accountId && accountId !== data?.id?.toString()) {
        navigate(`/${data?.isPartner ? 'partner' : 'client'}/${data?.id}/dashboard`);
      }
      return data;
    },
    select,
    placeholderData: keepPreviousData,
    // Middleware owns per-navigation refresh; only refetch on focus when an
    // account is in the path (idle / tab-return refresh).
    refetchOnMount: false,
    refetchOnWindowFocus: hasAccountInPath && !switchingAccount,
    enabled: (options?.enabled ?? true) && !switchingAccount,
  });
};

export type ResolvedClientSessionData = Exclude<
  Awaited<ReturnType<typeof handleGetClientSessionInfo>>,
  { isPartner: true }
>;

export type ResolvedPartnerSessionData = Exclude<
  Awaited<ReturnType<typeof handleGetClientSessionInfo>>,
  { isPartner: false }
>;

/**
 * # For Client (Advertiser) use only
 *
 * It will throw an error if you try to use it when you're logged in as a partner.
 *
 * If you want to use it in a partner component, use `usePartnerSessionQuery` instead (or `useSessionPlQuery` which is for both Client and Partner components)
 */
export function useClientSessionQuery(): UseQueryResult<ResolvedClientSessionData>;
export function useClientSessionQuery<TData = ResolvedClientSessionData>(
  select?: (data: ResolvedClientSessionData) => TData,
): UseQueryResult<TData>;
export function useClientSessionQuery<TData = ResolvedClientSessionData>(
  select?: (data: ResolvedClientSessionData) => TData,
): UseQueryResult<TData | ResolvedClientSessionData> {
  return useSessionPlQuery((data) => {
    if (data.isPartner)
      throw new Error(
        `useClientSessionQuery hook is for client pages only, and you're requesting it as a partner. Either use the partner version or useSessionPlQuery which is both`,
      );

    if (select) return select(data);

    return data;
  });
}

/**
 * # For Partner (Publisher) use only
 *
 *
 * It will throw an error if you try to use it when you're logged in as a client.
 *
 *
 * If you want to use it in a client component, use `useClientSessionQuery` instead (or `useSessionPlQuery` which is for both Client and Partner components)
 */
export function usePartnerSessionQuery(): UseQueryResult<ResolvedPartnerSessionData>;
export function usePartnerSessionQuery<TData = ResolvedPartnerSessionData>(
  select?: (data: ResolvedPartnerSessionData) => TData,
): UseQueryResult<TData>;
export function usePartnerSessionQuery<TData = ResolvedPartnerSessionData>(
  select?: (data: ResolvedPartnerSessionData) => TData,
): UseQueryResult<TData | ResolvedPartnerSessionData> {
  return useSessionPlQuery((data) => {
    if (!data.isPartner)
      throw new Error(
        "usePartnerSessionQuery  hook is for partner pages only, and you're requesting it as a client. Either use the client version or useSessionPlQuery which is both",
      );

    if (select) return select(data);
    return data;
  });
}

export default useSessionPlQuery;
